<template>
    <div class="recap-slider">
        <div ref="recapPagination" class="recap-slider__pagination">
            <template v-if="!isLoading">
                <button
                    v-for="item in itemCount"
                    :key="item"
                    class="recap-slider__pagination-item"
                    :class="paginationClasses(item)"
                    @click="handlePaginationItemClick(item)"
                >
                    <span :style="paginationProgress(item)">
                        {{ item }}
                    </span>
                </button>
            </template>
        </div>
        <div ref="recapCarousel" class="recap-slider__carousel">
            <IntroductionSlide v-if="isLoading" :isLoading="true" />
            <template v-else>
                <IntroductionSlide :isActive="currentIndex === 0 && !isFirstPlay" />
                <QuestionSlide
                    :question="$t.__('recap.slider.new_contacts.question')"
                    :isActive="currentIndex === 1"
                />
                <ZoomSlide
                    v-if="showNewContactsSlide"
                    :title="
                        $t.__('recap.slider.new_contacts', {
                            br: '<br>',
                        })
                    "
                    :listLine1="newContactsLine1"
                    :listLine2="newContactsLine2"
                    :isActive="currentIndex === 2"
                />
                <SellsyAcademySlide
                    v-else
                    :text="noNewContactsText"
                    url="https://go.sellsy.com/academy-videos/contacts-gestion-organisation-et-suivi"
                    :isActive="currentIndex === 2"
                />
                <QuestionSlide
                    :question="$t.__('recap.slider.top_cities.question')"
                    :isActive="currentIndex === 3"
                />
                <Top3Slide
                    v-if="formattedTopCities.length > 0"
                    :title="$t.__('recap.slider.top_cities.title')"
                    :list="formattedTopCities"
                    :isActive="currentIndex === 4"
                />
                <SellsyAcademySlide
                    v-else
                    :text="noNewContactsText"
                    url="https://help.sellsy.com/fr/articles/5866824-voir-les-societes-sur-une-carte"
                    :isActive="currentIndex === 4"
                />
                <QuestionSlide
                    :question="$t.__('recap.slider.activities.question')"
                    :isActive="currentIndex === 5"
                />
                <ZoomSlide
                    v-if="showActivitiesZoomSlide"
                    :title="
                        $t.__('recap.slider.activities_zoom.title', {
                            br: '<br>',
                        })
                    "
                    :listLine1="activitiesZoomLine1"
                    :listLine2="activitiesZoomLine2"
                    :isActive="currentIndex === 6"
                />
                <SellsyAcademySlide
                    v-else
                    :text="noActivitiesZoomText"
                    url="https://help.sellsy.com/fr/collections/3286091-suivez-l-activite-de-vos-equipes"
                    :isActive="currentIndex === 6"
                />
                <QuestionSlide
                    :question="$t.__('recap.slider.productive_month.question')"
                    :isActive="currentIndex === 7"
                />
                <TopMonthActivitiesSlide
                    v-if="showTopMonthActivities"
                    :title="$t.__('recap.slider.most_productive_month.title')"
                    :month="getMonth"
                    :activities="topMonthActivities"
                    :isActive="currentIndex === 8"
                />
                <SellsyAcademySlide
                    v-else
                    :text="noTopMonthActivitiesText"
                    url="/activities/all"
                    :isActive="currentIndex === 8"
                />
                <QuestionSlide
                    :question="$t.__('recap.slider.impact.question')"
                    :isActive="currentIndex === 9"
                />
                <Top1Slide
                    v-if="showImpactSlide"
                    :title="$t.__('recap.slider.annual_results.title')"
                    :icon="getImpactSlideInformation.icon"
                    :modelValue="getImpactSlideInformation.value"
                    :label="getImpactSlideInformation.label"
                    :isActive="currentIndex === 10"
                />
                <SellsyAcademySlide
                    v-else
                    :text="noImpactSlideText"
                    url="https://go.sellsy.com/academy?category=Facturation"
                    :isActive="currentIndex === 10"
                />
                <QuestionSlide
                    :question="$t.__('recap.slider.top3_months.question')"
                    :isActive="currentIndex === 11"
                />
                <Top3Slide
                    v-if="showBestMonths"
                    :title="$t.__('recap.slider.best_months.title')"
                    :list="formattedBestMonths"
                    :unit="data.currency"
                    :isActive="currentIndex === 12"
                />
                <SellsyAcademySlide
                    v-else
                    :text="noBestMonthsText"
                    url="https://help.sellsy.com/fr/articles/9127872-rapport-rentabilite-de-vos-opportunites"
                    :isActive="currentIndex === 12"
                />
                <QuestionSlide
                    :question="$t.__('recap.slider.best_clients.question')"
                    :isActive="currentIndex === 13"
                />
                <Top1Slide
                    v-if="showBestClient"
                    :title="$t.__('recap.slider.best_client.title')"
                    :icon="getBestClientInformation.icon"
                    :modelValue="getBestClientInformation.value"
                    :label="getBestClientInformation.label"
                    :isActive="currentIndex === 14"
                />
                <SellsyAcademySlide
                    v-else
                    :text="noBestClientText"
                    url="https://help.sellsy.com/fr/articles/7192257-rapport-les-clients-fideles-et-moins-fideles"
                    :isActive="currentIndex === 14"
                />
                <QuestionSlide
                    :question="$t.__('recap.slider.best_products.question')"
                    :isActive="currentIndex === 15"
                />
                <Top3Slide
                    v-if="showBestProducts"
                    :title="
                        $t.__('recap.slider.best_products.title', {
                            br: '<br>',
                        })
                    "
                    :list="formattedBestProducts"
                    :unit="data.currency"
                    :isActive="currentIndex === 16"
                />
                <SellsyAcademySlide
                    v-else
                    :text="noBestProductsText"
                    url="https://help.sellsy.com/fr/articles/5866573-ajouter-des-produits-et-services-a-mon-catalogue"
                    :isActive="currentIndex === 16"
                />
                <FinalSlide
                    :title="$t.__('recap.slider.FinalSlide.title1')"
                    :isActive="currentIndex === 17"
                />
                <FinalSlide
                    :title="$t.__('recap.slider.FinalSlide.title2')"
                    :hasButton="true"
                    :isActive="currentIndex === 18"
                />
            </template>
        </div>
        <div
            v-if="!isLoading"
            class="recap-slider__navigation-container recap-slider__navigation-container--prev"
        >
            <button
                ref="prevButton"
                class="recap-slider__navigation recap-slider__navigation--prev"
                :disabled="currentIndex === 0"
                @click="showPrev"
            >
                <CompassIcon icon="arrow-left" />
            </button>
        </div>
        <div
            v-if="!isLoading"
            class="recap-slider__navigation-container recap-slider__navigation-container--next"
        >
            <button
                ref="nextButton"
                class="recap-slider__navigation recap-slider__navigation--next"
                :disabled="currentIndex === itemCount - 1"
                @click="showNext"
            >
                <CompassIcon icon="arrow-right" />
            </button>
        </div>
    </div>
</template>
<script>
import IntroductionSlide from 'vcomponents/recap/slides/IntroductionSlide.vue';
import QuestionSlide from 'vcomponents/recap/slides/QuestionSlide.vue';
import Top3Slide from 'vcomponents/recap/slides/Top3Slide.vue';
import SellsyAcademySlide from 'vcomponents/recap/slides/SellsyAcademySlide.vue';
import ZoomSlide from 'vcomponents/recap/slides/ZoomSlide.vue';
import TopMonthActivitiesSlide from 'vcomponents/recap/slides/TopMonthActivitiesSlide.vue';
import Top1Slide from 'vcomponents/recap/slides/Top1Slide.vue';
import FinalSlide from 'vcomponents/recap/slides/FinalSlide.vue';

export default {
    name: 'RecapSlider',
    components: {
        IntroductionSlide,
        QuestionSlide,
        Top3Slide,
        SellsyAcademySlide,
        ZoomSlide,
        TopMonthActivitiesSlide,
        Top1Slide,
        FinalSlide,
    },
    // TODO: dynamic itemCount
    data() {
        return {
            isLoading: true,
            isFirstPlay: true,
            itemCount: 19,
            currentIndex: 0,
            autoSlide: null,
            currentSlideProgress: 0,
            startTime: null,
            elapsedTime: 0,
            intervalDuration: 10000,
            itemActiveClass: 'slider-item--active',
            data: {},
            noNewContactsText: [
                this.$t.__('recap.slider.new_contacts.SellsyAcademySlide.text1'),
                this.$t.__('recap.slider.new_contacts.SellsyAcademySlide.text2'),
            ],
            noTopCitiesText: [
                this.$t.__('recap.slider.top_cities.SellsyAcademySlide.text1'),
                this.$t.__('recap.slider.top_cities.SellsyAcademySlide.text2'),
            ],
            noActivitiesZoomText: [
                this.$t.__('recap.slider.activities_zoom.SellsyAcademySlide.text1'),
                this.$t.__('recap.slider.activities_zoom.SellsyAcademySlide.text2'),
            ],
            noTopMonthActivitiesText: [
                this.$t.__('recap.slider.top_month.SellsyAcademySlide.text1'),
                this.$t.__('recap.slider.top_month.SellsyAcademySlide.text2'),
            ],
            noImpactSlideText: [
                this.$t.__('recap.slider.impact.SellsyAcademySlide.text1'),
                this.$t.__('recap.slider.impact.SellsyAcademySlide.text2'),
            ],
            noBestMonthsText: [
                this.$t.__('recap.slider.best_months.SellsyAcademySlide.text1'),
                this.$t.__('recap.slider.best_months.SellsyAcademySlide.text2'),
            ],
            noBestClientText: [
                this.$t.__('recap.slider.best_client.SellsyAcademySlide.text1'),
                this.$t.__('recap.slider.best_client.SellsyAcademySlide.text2'),
            ],
            noBestProductsText: [
                this.$t.__('recap.slider.best_products.SellsyAcademySlide.text1'),
                this.$t.__('recap.slider.best_products.SellsyAcademySlide.text2'),
            ],
        };
    },
    computed: {
        items() {
            return this.$refs.recapCarousel?.children;
        },
        paginationProgress() {
            return (paginationItemIndex) => {
                return {
                    width: `${
                        this.currentIndex > paginationItemIndex - 1 ||
                        (paginationItemIndex > this.currentIndex && this.autoSlide === null)
                            ? '100'
                            : this.currentSlideProgress
                    }%`,
                };
            };
        },
        paginationClasses() {
            return (paginationItemIndex) => {
                return {
                    'recap-slider__pagination-item--active':
                        this.currentIndex === paginationItemIndex - 1,
                    'recap-slider__pagination-item--completed':
                        this.currentIndex > paginationItemIndex - 1 ||
                        (paginationItemIndex > this.currentIndex && this.autoSlide === null),
                };
            };
        },
        newContactsLine1() {
            return [
                {
                    icon: 'individual',
                    number:
                        this.data['prospects.created'] > 0
                            ? `+ ${this.data['prospects.created']}`
                            : this.data['prospects.created'],
                    label: this.$t.n__(
                        'common.labels.prospects',
                        null,
                        this.data['prospects.created'],
                        this.data['prospects.created'],
                    ),
                },
            ];
        },
        newContactsLine2() {
            return [
                {
                    icon: 'phone',
                    number:
                        this.data['clients.created'] > 0
                            ? `+ ${this.data['clients.created']}`
                            : this.data['clients.created'],
                    label: this.$t.n__(
                        'common.labels.clients',
                        null,
                        this.data['clients.created'],
                        this.data['clients.created'],
                    ),
                },
                {
                    icon: 'avatar',
                    number:
                        this.data['contacts.created'] > 0
                            ? `+ ${this.data['contacts.created']}`
                            : this.data['contacts.created'],
                    label: this.$t.n__(
                        'common.labels.contacts',
                        null,
                        this.data['contacts.created'],
                        this.data['contacts.created'],
                    ),
                },
            ];
        },
        showNewContactsSlide() {
            return (
                this.data['prospects.created'] > 0 ||
                this.data['clients.created'] > 0 ||
                this.data['contacts.created'] > 0
            );
        },
        formattedTopCities() {
            return Object.entries(this.data['cities.top']).map(([city, value]) => ({
                name: city,
                value,
                unit: this.$t.n__('common.labels.contacts', null, value, value),
            }));
        },
        activitiesZoomLine1() {
            return [
                {
                    icon: 'task',
                    number:
                        this.data['task.completed'] > 0
                            ? `+ ${this.data['task.completed']}`
                            : this.data['task.completed'],
                    label: this.$t.n__(
                        'common.labels.tasks_completed',
                        null,
                        this.data['task.completed'],
                        this.data['task.completed'],
                    ),
                },
                {
                    icon: 'calendar',
                    number:
                        this.data['events.created'] > 0
                            ? `+ ${this.data['events.created']}`
                            : this.data['events.created'],
                    label: this.$t.n__(
                        'common.labels.meetings_held',
                        null,
                        this.data['events.created'],
                        this.data['events.created'],
                    ),
                },
            ];
        },
        activitiesZoomLine2() {
            return [
                {
                    icon: 'phone',
                    number:
                        this.data['phonecall.created'] > 0
                            ? `+ ${this.data['phonecall.created']}`
                            : this.data['phonecall.created'],
                    label: this.$t.n__(
                        'common.labels.calls_made',
                        null,
                        this.data['phonecall.created'],
                        this.data['phonecall.created'],
                    ),
                },
                {
                    icon: 'email',
                    number:
                        this.data['email.sent'] > 0
                            ? `+ ${this.data['email.sent']}`
                            : this.data['email.sent'],
                    label: this.$t.n__(
                        'common.labels.emails_sent',
                        null,
                        this.data['email.sent'],
                        this.data['email.sent'],
                    ),
                },
            ];
        },
        showActivitiesZoomSlide() {
            return (
                this.data['task.completed'] > 0 ||
                this.data['events.created'] > 0 ||
                this.data['phonecall.created'] > 0 ||
                this.data['email.sent'] > 0
            );
        },
        getMonth() {
            return moment(this.data['activities.top_month'].label).format('MMMM');
        },
        topMonthActivities() {
            return [
                {
                    icon: 'task',
                    number:
                        this.data['activities.top_month']['task.completed'] > 0
                            ? `+ ${this.data['activities.top_month']['task.completed']}`
                            : (this.data['activities.top_month']['task.completed'] ?? 0),
                    label: this.$t.n__(
                        'common.labels.tasks_completed',
                        null,
                        this.data['activities.top_month']['task.completed'],
                        this.data['activities.top_month']['task.completed'],
                    ),
                },
                {
                    icon: 'calendar',
                    number:
                        this.data['activities.top_month']['event.created'] > 0
                            ? `+ ${this.data['activities.top_month']['event.created']}`
                            : (this.data['activities.top_month']['event.created'] ?? 0),
                    label: this.$t.n__(
                        'common.labels.meetings_held',
                        null,
                        this.data['activities.top_month']['event.created'],
                        this.data['activities.top_month']['event.created'],
                    ),
                },
                {
                    icon: 'phone',
                    number:
                        this.data['activities.top_month']['phonecall.created'] > 0
                            ? `+ ${this.data['activities.top_month']['phonecall.created']}`
                            : (this.data['activities.top_month']['phonecall.created'] ?? 0),
                    label: this.$t.n__(
                        'common.labels.calls_made',
                        null,
                        this.data['activities.top_month']['phonecall.created'],
                        this.data['activities.top_month']['phonecall.created'],
                    ),
                },
                {
                    icon: 'email',
                    number:
                        this.data['activities.top_month']['email.sent'] > 0
                            ? `+ ${this.data['activities.top_month']['email.sent']}`
                            : (this.data['activities.top_month']['email.sent'] ?? 0),
                    label: this.$t.n__(
                        'common.labels.emails_sent',
                        null,
                        this.data['activities.top_month']['email.sent'],
                        this.data['activities.top_month']['email.sent'],
                    ),
                },
            ];
        },
        showTopMonthActivities() {
            return (
                this.data['activities.top_month'] &&
                (this.data['activities.top_month']['task.completed'] > 0 ||
                    this.data['activities.top_month']['event.created'] > 0 ||
                    this.data['activities.top_month']['phonecall.created'] > 0 ||
                    this.data['activities.top_month']['email.sent'] > 0)
            );
        },
        hasInvoices() {
            return this.data.sales && this.data.sales.length > 0;
        },
        hasEstimates() {
            return this.data.estimates && this.data.estimates.length > 0;
        },
        hasOpportunities() {
            return this.data.opportunities && this.data.opportunities.length > 0;
        },
        showImpactSlide() {
            return (
                (this.hasInvoices || this.hasEstimates || this.hasOpportunities) &&
                this.getImpactSlideInformation.value > 0
            );
        },
        getImpactSlideInformation() {
            const output = {
                icon: 'opportunity',
                value: 0,
            };

            switch (true) {
                case this.hasInvoices:
                    output.icon = 'invoicing';
                    output.value = this.data.sales.find((item) => item.card === 'default')?.cnt;
                    output.label = this.$t.n__(
                        'common.labels.invoices_generated',
                        null,
                        this.data.sales.find((item) => item.card === 'default')?.cnt,
                        this.data.sales.find((item) => item.card === 'default')?.cnt,
                    );
                    break;
                case this.hasEstimates:
                    output.icon = 'payment';
                    output.value = this.data.estimates.find((item) => item.card === 'default')?.cnt;
                    output.label = this.$t.n__(
                        'common.labels.estimates_accepted',
                        null,
                        this.data.estimates.find((item) => item.card === 'default')?.cnt,
                        this.data.estimates.find((item) => item.card === 'default')?.cnt,
                    );
                    break;
                case this.hasOpportunities:
                    output.value = this.data.opportunities.find(
                        (item) => item.card === 'default',
                    )?.cnt;
                    output.label = this.$t.n__(
                        'common.labels.opportunities_won',
                        null,
                        this.data.opportunities.find((item) => item.card === 'default')?.cnt,
                        this.data.opportunities.find((item) => item.card === 'default')?.cnt,
                    );
                    break;
                default:
                    break;
            }
            return output;
        },
        formattedBestMonths() {
            const dataSources = [
                { hasData: this.hasInvoices, data: this.data.sales },
                { hasData: this.hasEstimates, data: this.data.estimates },
                { hasData: this.hasOpportunities, data: this.data.opportunities },
            ];

            for (const source of dataSources) {
                if (source.hasData) {
                    return source.data
                        .filter((item) => item.card === 'month')
                        .map((item) => ({
                            value: this.$filters.numberFormatWithOptions(
                                item.cnt,
                                { precision: 0 },
                                false,
                            ),
                            name: moment(item.label).format('MMMM'),
                        }))
                        .sort((a, b) => b.rank - a.rank);
                }
            }

            return [];
        },
        showBestMonths() {
            return this.formattedBestMonths.length > 0;
        },
        getBestClientInformation() {
            const output = {
                icon: 'company',
                value: '',
                label: '',
            };

            const dataSources = [
                { hasData: this.hasInvoices, data: this.data.sales },
                { hasData: this.hasEstimates, data: this.data.estimates },
                { hasData: this.hasOpportunities, data: this.data.opportunities },
            ];

            for (const source of dataSources) {
                if (source.hasData) {
                    const topItem = source.data.find(
                        (item) => item.card === 'third' && item.rank === '1',
                    );
                    if (topItem) {
                        output.label = this.getFormattedAmountForBestClient(topItem.cnt);
                        output.value = topItem.label;
                        break;
                    }
                }
            }

            return output;
        },
        showBestClient() {
            return this.getBestClientInformation.value;
        },
        formattedBestProducts() {
            const dataSources = [
                { hasData: this.hasInvoices, data: this.data.sales },
                { hasData: this.hasEstimates, data: this.data.estimates },
                { hasData: this.hasOpportunities, data: this.data.opportunities },
            ];

            for (const source of dataSources) {
                if (
                    source.hasData &&
                    source.data.filter((item) => item.card === 'product').length
                ) {
                    return source.data
                        .filter((item) => item.card === 'product')
                        .map((item) => ({
                            name: item.label,
                            value: this.$filters.numberFormatWithOptions(
                                item.cnt,
                                { precision: 0 },
                                false,
                            ),
                        }));
                }
            }

            return [];
        },
        showBestProducts() {
            return this.formattedBestProducts.length > 0;
        },
    },
    created() {
        window.SellsyJS.WebServiceCaller.get(
            '/rest/meta/retrospective',
            {},
            (response) => {
                this.data = response;
                this.isLoading = false;
            },
            () => {
                this.$pushToast({
                    type: 'error',
                    icon: true,
                    close: true,
                    text: this.$t.__('recap.slider.error'),
                });
                this.isLoading = false;
            },
        );
    },
    mounted() {
        const interval = setInterval(() => {
            if (!this.isLoading) {
                this.startAutoSlide();
                clearInterval(interval);

                this.sendPendoTrackEvent();
            }
        }, 200);
    },
    beforeUnmount() {
        clearInterval(this.autoSlide);
    },
    methods: {
        sendPendoTrackEvent() {
            if (window.pendo?.accountId) {
                window.pendo.track('recap2024', {
                    progress: `${this.currentIndex + 1}/${this.itemCount}`,
                });
            }
        },
        updateCarousel() {
            this.items[this.currentIndex].classList.add(this.itemActiveClass);
        },
        handlePaginationItemClick(index) {
            this.items[this.currentIndex].classList.remove(this.itemActiveClass);
            this.currentIndex = index - 1;
            this.updateCarousel();
            this.restartAutoSlide();
        },
        showNext() {
            if (this.currentIndex < this.itemCount - 1) {
                this.items[this.currentIndex].classList.remove(this.itemActiveClass);
                this.currentIndex++;
                this.isFirstPlay = false;

                this.sendPendoTrackEvent();

                this.updateCarousel();
                this.restartAutoSlide();
            } else if (this.currentIndex === this.itemCount - 1) {
                clearInterval(this.autoSlide);
                this.autoSlide = null;
            }
        },
        showPrev() {
            if (this.currentIndex <= this.itemCount - 1 && this.currentIndex > 0) {
                this.items[this.currentIndex].classList.remove(this.itemActiveClass);
                this.currentIndex--;
                this.updateCarousel();
                this.restartAutoSlide();
            }
        },
        restartAutoSlide() {
            this.stopAutoSlide();
            this.startAutoSlide();
        },
        startAutoSlide() {
            this.resetProgress();
            this.startTime = Date.now();
            this.autoSlide = setInterval(() => {
                const now = Date.now();
                this.elapsedTime = now - this.startTime;
                this.currentSlideProgress = (this.elapsedTime / this.intervalDuration) * 100;

                if (this.elapsedTime >= this.intervalDuration) {
                    this.showNext();
                    this.resetProgress();
                }
            }, 10); // Update progress every 10ms
        },
        stopAutoSlide() {
            clearInterval(this.autoSlide);
            this.autoSlide = null;
        },
        resetProgress() {
            this.currentSlideProgress = 0;
            this.startTime = Date.now();
            this.elapsedTime = 0;
        },
        getFormattedAmountForBestClient(amount) {
            const formattedAmount = this.$filters.numberFormatWithOptions(
                amount,
                { precision: 0 },
                false,
            );

            return `${this.$t.__('common.labels.with')} ${formattedAmount} ${this.data.currency}`;
        },
    },
};
</script>
<style lang="scss" scoped>
.recap-slider {
    padding: 0 71px;
    position: relative;

    &__pagination {
        display: flex;
        gap: var(--spacing-0);
        height: 3px;
        margin: 0 0 calc(var(--spacing-1) - 1px);
        padding: 0 14px;

        &-item {
            background: #ffffff4d;
            border: 0;
            border-radius: 30px;
            cursor: pointer;
            display: block;
            flex: 1;
            height: 3px;
            overflow: hidden;
            padding: 0;
            position: relative;

            span {
                background: #fff;
                border-radius: 30px;
                display: none;
                height: 100%;
                left: 0;
                position: absolute;
                text-indent: -9999em;
                top: 0;
            }

            &--active,
            &--completed {
                span {
                    display: block;
                }
            }
        }
    }

    &__carousel {
        border: 1px solid #edf0ff5e;
        border-radius: 10px;
        height: 564px;
        overflow: hidden;
        position: relative;
        width: 360px;
    }

    &__navigation {
        &-container {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);

            &--prev {
                left: 0;
            }

            &--next {
                right: 0;
            }
        }

        align-items: center;
        background: none;
        border: 1px solid #fff;
        border-radius: 50%;
        color: #fff;
        cursor: pointer;
        display: flex;
        height: 40px;
        justify-content: center;
        transition: all 200ms ease-in-out;
        transform: scale(1);
        width: 40px;

        &--prev {
            transform-origin: center right;
        }

        &--next {
            transform-origin: center left;
        }

        &:hover {
            transform: scale(1.1);
        }

        &:disabled {
            cursor: default;
            opacity: 0.4;

            &:hover {
                transform: scale(1);
            }
        }
    }

    :deep(.slider-item) {
        align-items: center;
        bottom: 0;
        display: none;
        flex-direction: column;
        justify-content: center;
        left: 0;
        overflow: hidden;
        padding: var(--spacing-6) 30px;
        position: absolute;
        right: 0;
        top: 0;

        color: #fff;

        .slider-item--active {
            display: flex;
        }
    }
}
</style>
